/**
 * Shared deal & schedule filtering for all views
 */

export function filterDealsByAttributes(deals, filters) {
    return deals.filter(deal => {
        if (filters.stage && deal.dealStage !== filters.stage) return false;
        if (filters.pipeline && deal.dealPipeline !== filters.pipeline) return false;
        if (filters.frequency && deal.invoiceFrequency !== filters.frequency) return false;
        if (filters.owner && !deal.dealOwner?.toLowerCase().includes(filters.owner.toLowerCase())) return false;
        if (filters.account && !deal.accountName?.toLowerCase().includes(filters.account.toLowerCase())) return false;
        return true;
    });
}

export function scheduleMatchesDateFilter(schedule, filters) {
    if (!filters.startDate && !filters.endDate && !(filters.quarters?.length && filters.filterYear)) {
        return true;
    }

    const invoiceDate = schedule.invoiceDate;
    const dueDate = schedule.dueDate;
    const dateStr = invoiceDate || dueDate;
    if (!dateStr) return false;

    // Normalize to a Date for quarter filtering.
    const d = new Date(dateStr + 'T12:00:00');

    if (filters.quarters?.length && filters.filterYear) {
        const q = `Q${Math.floor(d.getMonth() / 3) + 1}`;
        if (d.getFullYear() !== filters.filterYear || !filters.quarters.includes(q)) return false;
        return true;
    }

    if (filters.startDate && dateStr < filters.startDate) return false;
    if (filters.endDate && dateStr > filters.endDate) return false;
    return true;
}


export function scheduleMatchesStatusFilter(schedule, filters) {
    const status = schedule.status || 'planned';
    if (filters.includePlanned === false && status === 'planned') return false;
    if (filters.includeInvoiced === false && status === 'invoiced') return false;
    if (filters.includePaid === false && status === 'paid') return false;
    if (filters.includeAtRisk === false && (status === 'cancelled' || status === 'overdue' || status === 'at_risk')) return false;
    return true;
}

export function scheduleMatchesFilters(schedule, filters) {
    return scheduleMatchesDateFilter(schedule, filters) && scheduleMatchesStatusFilter(schedule, filters);
}

export function getDealDateBounds(deal, schedules = []) {
    const sorted = [...schedules].sort((a, b) => a.invoiceDate.localeCompare(b.invoiceDate));
    const start = sorted[0]?.invoiceDate
        || deal.expectedFirstInvoiceDate
        || deal.firstInvoiceDate;
    const end = sorted[sorted.length - 1]?.invoiceDate
        || deal.expectedLastInvoiceDate
        || deal.lastInvoiceDate;
    return { start, end };
}

export function dateRangeOverlaps(startA, endA, startB, endB) {
    if (!startA || !endA || !startB || !endB) return true;
    return endA >= startB && startA <= endB;
}

export function dealMatchesDateFilter(deal, schedules, filters) {
    if (!filters.startDate && !filters.endDate && !(filters.quarters?.length && filters.filterYear)) {
        return true;
    }

    const dealSchedules = schedules.filter(s => s.dealId === deal.id);
    if (dealSchedules.some(s => scheduleMatchesDateFilter(s, filters))) {
        return true;
    }

    const { start, end } = getDealDateBounds(deal, dealSchedules);
    if (!start || !end) return false;

    if (filters.quarters?.length && filters.filterYear) {
        const dealStart = new Date(start + 'T00:00:00');
        const dealEnd = new Date(end + 'T23:59:59');
        return filters.quarters.some(q => {
            const qNum = parseInt(q.replace('Q', ''), 10) - 1;
            const qStart = new Date(filters.filterYear, qNum * 3, 1);
            const qEnd = new Date(filters.filterYear, qNum * 3 + 3, 23, 59, 59);
            return dateRangeOverlaps(start, end, qStart.toISOString().split('T')[0], qEnd.toISOString().split('T')[0]);
        });
    }

    const rangeStart = filters.startDate;
    const rangeEnd = filters.endDate;
    return dateRangeOverlaps(start, end, rangeStart, rangeEnd);
}

export function filterDeals(deals, schedules, filters) {
    const byAttrs = filterDealsByAttributes(deals, filters);
    return byAttrs.filter(deal => dealMatchesDateFilter(deal, schedules, filters));
}
